import { PayloadAction } from "@reduxjs/toolkit";
import {
  InsertUpdatedSecurityValues,
  SecuritiesInStagingState,
  SecuritiesUpdatedStagedState,
  SecuritiesValuesState,
} from "./types";
import { YieldType, Environment } from "../globalTypes";
import { PricesResendManager } from "../../classes/pricesResendManager";

const ADAPT_TO_ZERO_INDEX = 2;

const setStagingState = (state: SecuritiesUpdatedStagedState, action: PayloadAction<SecuritiesInStagingState>) => {
  state.stagedSecuritiesValuesUpdates = action.payload;
};

const updateRowsInStagingAreaAfterDeletingSecurities = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<{ deletedRows: number[] | null }>
) => {
  const { deletedRows } = action.payload;
  const securitiesInStaging = state.stagedSecuritiesValuesUpdates;

  if (!deletedRows) return;

  deletedRows.sort((a, b) => b + a);

  Object.keys(securitiesInStaging).forEach((securityProducerId) => {
    const security = securitiesInStaging[securityProducerId];
    let securityRow = security.row;

    deletedRows.forEach((deletedRow, idx) => {
      const recalculatedRowDeleted = deletedRow - idx;

      if (recalculatedRowDeleted > securityRow - ADAPT_TO_ZERO_INDEX) return;

      securityRow--;
      security.row = securityRow;
    });
  });
};

const removeSecurityFromStagingAndSecuritiesValues = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<{ isin: string }>
) => {
  const { isin } = action.payload;

  Object.keys(state.stagedSecuritiesValuesUpdates).forEach((securityProducerId) => {
    if (state.stagedSecuritiesValuesUpdates[securityProducerId].isin === isin) {
      delete state.stagedSecuritiesValuesUpdates[securityProducerId];
    }
  });

  Object.keys(state.securitiesValues).forEach((securityProducerId) => {
    if (state.securitiesValues[securityProducerId].isin === isin) {
      delete state.securitiesValues[securityProducerId];
    }
  });
};

const setSecuritiesValuesState = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<SecuritiesValuesState>
) => {
  state.securitiesValues = action.payload;
};

const addSecurityToStagingArea = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<InsertUpdatedSecurityValues>
) => {
  const { securityProducerId, ...securityProducer } = action.payload;

  const securityInStaging = state.stagedSecuritiesValuesUpdates[securityProducerId];

  if (!securityInStaging) {
    state.stagedSecuritiesValuesUpdates[securityProducerId] = securityProducer;
    return;
  }

  const bidOfSecurityInStaging = securityInStaging.bid;
  const askOfSecurityInStaging = securityInStaging.ask;

  const bidFromUpdate = securityProducer.bid;
  const askFromUpdate = securityProducer.ask;

  state.stagedSecuritiesValuesUpdates[securityProducerId] = securityProducer;

  if (!bidFromUpdate && bidOfSecurityInStaging)
    state.stagedSecuritiesValuesUpdates[securityProducerId].bid = bidOfSecurityInStaging;

  if (!askFromUpdate && askOfSecurityInStaging)
    state.stagedSecuritiesValuesUpdates[securityProducerId].ask = askOfSecurityInStaging;
};

const removeSecurityFromStagingArea = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<{ securityProducerId: string }>
) => {
  const { securityProducerId } = action.payload;

  delete state.stagedSecuritiesValuesUpdates[securityProducerId];
};

const setQuoteFeedOfSecurityInPricesSentAsNone = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<{ securityProducerId: string }>
) => {
  const { securityProducerId } = action.payload;

  const securityInPricesSent = state.securitiesValues[securityProducerId];

  delete state.stagedSecuritiesValuesUpdates[securityProducerId];

  if (!securityInPricesSent) return;

  securityInPricesSent.quoteFeedId = "none";
  securityInPricesSent.quoteFeedName = "None";
};

const moveSecurityFromStagingToSecuritiesValues = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<InsertUpdatedSecurityValues>
) => {
  const { securityProducerId, ...data } = action.payload;

  const bidFromSecuritiesValues = state.securitiesValues[securityProducerId]?.bid;
  const askFromSecuritiesValues = state.securitiesValues[securityProducerId]?.ask;

  state.securitiesValues[securityProducerId] = data;

  if (bidFromSecuritiesValues && !data.bid) {
    state.securitiesValues[securityProducerId].bid = bidFromSecuritiesValues;
  }

  if (askFromSecuritiesValues && !data.ask) {
    state.securitiesValues[securityProducerId].ask = askFromSecuritiesValues;
  }

  delete state.stagedSecuritiesValuesUpdates[securityProducerId];
};

const managePricesWithErrorSending = (
  state: SecuritiesUpdatedStagedState,
  action: PayloadAction<{
    updatedValues: InsertUpdatedSecurityValues;
    yieldsWithErrors: { yieldType: YieldType; statusCode: number }[];
    environment: Environment;
  }>
) => {
  const { updatedValues, yieldsWithErrors, environment } = action.payload;

  const { securityProducerId, ...data } = updatedValues;
  const pricesResendManager = PricesResendManager.getInstance();

  if (yieldsWithErrors.length > 1) {
    const statusCodes = {
      bid: yieldsWithErrors.find((error) => error.yieldType === YieldType.BID)?.statusCode,
      ask: yieldsWithErrors.find((error) => error.yieldType === YieldType.ASK)?.statusCode,
    };

    pricesResendManager.addError({
      error: { ...data, securityProducerId },
      statusCodes,
      statusAsk: statusCodes.ask || undefined,
      statusBid: statusCodes.bid || undefined,
    });
    delete state.stagedSecuritiesValuesUpdates[securityProducerId];

    return;
  }

  const statusCodes = {
    bid: yieldsWithErrors.find((error) => error.yieldType === YieldType.BID)?.statusCode,
    ask: yieldsWithErrors.find((error) => error.yieldType === YieldType.ASK)?.statusCode,
  };

  const yieldWithErrorSending = yieldsWithErrors[0].yieldType;
  const yieldWithNoErrorSending = yieldWithErrorSending === YieldType.ASK ? YieldType.BID : YieldType.ASK;

  const securityInformationWithNoErrorSending = { ...data };

  delete securityInformationWithNoErrorSending[yieldWithNoErrorSending];

  pricesResendManager.addError({
    error: { ...securityInformationWithNoErrorSending, securityProducerId },
    statusCodes,
  });

  if (!state.securitiesValues[securityProducerId] && !data[yieldWithNoErrorSending]) return;

  delete state.stagedSecuritiesValuesUpdates[securityProducerId];

  state.securitiesValues[securityProducerId] = data;
  state.securitiesValues[securityProducerId][yieldWithErrorSending] = "";
};

export default {
  setStagingState,
  removeSecurityFromStagingAndSecuritiesValues,
  setSecuritiesValuesState,
  addSecurityToStagingArea,
  moveSecurityFromStagingToSecuritiesValues,
  managePricesWithErrorSending,
  updateRowsInStagingAreaAfterDeletingSecurities,
  removeSecurityFromStagingArea,
  setQuoteFeedOfSecurityInPricesSentAsNone,
};
